import React, { FC } from "react";

import { Text } from "@UI/Components/Text/Text";
import useI18n from "@I18n/hooks/useI18n";
import messages from "./messages";

/**
 * What `Tasks::HasModeration` leaves behind where it hid a passage. Matched by
 * shape rather than by rule name, so a new backend rule needs no change here.
 * Consecutive markers collapse into one run: two hidden passages sitting side
 * by side read as one label, not two.
 */
const REDACTION_MARKER = /(?:\[redacted_\w+\])+/g;

/** Whether any of these texts carries a hidden passage. */
export const hasModeratedContent = (
  ...texts: Array<string | null | undefined>
): boolean =>
  texts.some(
    (text) => typeof text === "string" && text.search(REDACTION_MARKER) !== -1
  );

/** The marker replaced by the label, for the places that need a plain string
 * (a tooltip label, an `alt`) rather than the styled inline version. */
export const moderatedPlainText = (
  text: string | null | undefined,
  label: string
): string | undefined => text?.replace(REDACTION_MARKER, label);

interface ModeratedInlineProps {
  /** Absent while the task is still loading, so callers can pass it straight
   * through rather than each guarding the field itself. */
  text: string | null | undefined;
  /** Label color. Darker than the body copy on white; on the lightbox's dark
   * backdrop that inverts, so it is a prop rather than a fixed shade. */
  labelColor?: string;
}

/** Task text with its hidden passages shown as an inline label, carrying no
 * styling of its own — for a caller that already owns the surrounding type
 * (the card's `Heading`, say). Most callers want `ModeratedText`. */
export const ModeratedInline: FC<ModeratedInlineProps> = ({
  text,
  labelColor = "gray.900",
}) => {
  const { formatMessage } = useI18n();
  const label = formatMessage(messages["moderatedContent.inlineLabel"]);

  if (!text) return null;

  return (
    <>
      {text.split(REDACTION_MARKER).map((part, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          {index > 0 && (
            <Text as="span" fontStyle="italic" color={labelColor}>
              {label}
            </Text>
          )}
          {part}
        </React.Fragment>
      ))}
    </>
  );
};

interface ModeratedTextProps extends ModeratedInlineProps {
  /** Body-copy color. The lightbox sits on a dark backdrop and passes "white". */
  color?: string;
}

/**
 * The body-copy form: `ModeratedInline` in the same `Text` a `DetailLine`
 * would give a plain-string detail. `DetailLine` only styles a detail it
 * receives as a string, so a JSX detail has to bring its own.
 */
export const ModeratedText: FC<ModeratedTextProps> = ({
  text,
  color = "gray.700",
  labelColor,
}) =>
  text ? (
    <Text m="0" w="full" fontSize="md" color={color} whiteSpace="pre-line">
      <ModeratedInline text={text} labelColor={labelColor} />
    </Text>
  ) : null;
